import type { ProductColor, RecommendedCompsStatistics } from '@aloft/toolkit-shared';
import { Flex, Typography } from '@aloft/toolkit-sui';

import { PropertyPinIcon } from '../../../PropertyPin.tsx';
import type { CompSelectionProperty } from '../../types.ts';
import { calculateCompSelectionStatistics } from '../../utils/calculateCompSelectionStatistics.ts';

const formatCount = (isLoading: boolean, value?: number) =>
  !isLoading && value !== undefined ? value.toString() : '-';

const SaleWindowStat = ({
  label,
  value,
  isLoading,
}: {
  label: string;
  value?: number;
  isLoading: boolean;
}) => (
  <Flex alignItems="center" gap="4px">
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="body2" color="textPrimary">
      {formatCount(isLoading, value)}
    </Typography>
  </Flex>
);

const StatusStat = ({
  pinColor,
  pinText,
  label,
  value,
  isLoading,
}: {
  pinColor: ProductColor;
  pinText: string;
  label: string;
  value?: number;
  isLoading: boolean;
}) => (
  <Flex alignItems="center">
    <PropertyPinIcon color={pinColor} text={pinText} />
    <Typography variant="body1" color="textSecondary">
      {label}&nbsp;
    </Typography>
    <Typography variant="body1">{formatCount(isLoading, value)}</Typography>
  </Flex>
);

export const FilterStatistics = ({
  comps,
  statistics,
  isLoading,
  showStatusCounts = true,
}: {
  comps?: CompSelectionProperty[];
  statistics?: RecommendedCompsStatistics;
  isLoading: boolean;
  showStatusCounts?: boolean;
}) => {
  const resolvedStatistics = comps ? calculateCompSelectionStatistics(comps) : statistics;

  return (
    <Flex alignItems="center" gap="16px" whiteSpace="nowrap">
      {showStatusCounts && (
        <>
          <StatusStat
            pinColor="pinSold"
            pinText="S"
            label="Sold:"
            value={resolvedStatistics?.listingsSoldCount}
            isLoading={isLoading}
          />
          <StatusStat
            pinColor="pinActive"
            pinText="A"
            label="Active:"
            value={resolvedStatistics?.listingsActiveCount}
            isLoading={isLoading}
          />
          <StatusStat
            pinColor="pinPending"
            pinText="P"
            label="Pending:"
            value={resolvedStatistics?.listingsPendingCount}
            isLoading={isLoading}
          />
        </>
      )}
      <Typography variant="body2" color="textPrimary">
        Sales in the last:
      </Typography>
      <SaleWindowStat
        label="0-3 months:"
        value={resolvedStatistics?.last3MonthsListingsCount}
        isLoading={isLoading}
      />
      <SaleWindowStat
        label="4-6 months:"
        value={resolvedStatistics?.last6MonthsListingsCount}
        isLoading={isLoading}
      />
      <SaleWindowStat
        label="7-12 months:"
        value={resolvedStatistics?.last12MonthsListingsCount}
        isLoading={isLoading}
      />
    </Flex>
  );
};
